import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Flex, H5, M, PageSizeType } from 'design-system-web';
import { WorkOrderModuleCategoriesSettings } from 'lane-shared/domains/workOrder';
import { Channel } from 'application-types';
import { CreateCoverageHoursPanel } from './CreateCoverageHoursPanel';
import { CoverageHoursTable } from './CoverageHoursTable';
import { useCoverageHoursPermissions } from './useCoverageHoursPermissions';
import {
  COVERAGE_HOURS_PAGE_SIZE,
  CoverageHoursEntry,
  useCoverageHours,
} from './useCoverageHours';
import styles from './CoverageHours.scss';

type Props = {
  channel?: Channel;
  categories?: WorkOrderModuleCategoriesSettings[];
};

export function CoverageHours({ channel, categories = [] }: Props) {
  const { t } = useTranslation();
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState<PageSizeType>(
    COVERAGE_HOURS_PAGE_SIZE
  );
  // Rows added locally right after Save, before the post-create refetch lands.
  const [optimisticEntries, setOptimisticEntries] = useState<
    CoverageHoursEntry[]
  >([]);
  const { canManage } = useCoverageHoursPermissions({
    channelId: channel?._id,
  });

  const { entries, totalRows, loading, error, fetchCoverageHours } =
    useCoverageHours({
      channelId: channel?._id ?? '',
      pageIndex: page,
      perPage: pageSize,
    });

  useEffect(() => {
    if (channel?._id) {
      fetchCoverageHours();
    }
  }, [channel?._id, fetchCoverageHours]);

  const handleOpenPanel = () => {
    if (!channel?._id) {
      window.Toast.show(
        t(
          'web.admin.serviceRequest.settings.coverageHours.create.channelError'
        )
      );

      return;
    }

    setIsPanelOpen(true);
  };

  const handleClosePanel = () => setIsPanelOpen(false);

  const handlePageSizeChange = (nextPageSize: PageSizeType) => {
    setPageSize(nextPageSize);
    setPage(0);
  };

  const handleOptimisticAdd = useCallback((rows: CoverageHoursEntry[]) => {
    setOptimisticEntries(prev => [...prev, ...rows]);
  }, []);

  const handleCreateSuccess = useCallback(() => {
    setOptimisticEntries([]);
    fetchCoverageHours();
  }, [fetchCoverageHours]);

  const handleCreateError = useCallback((localIds: string[]) => {
    setOptimisticEntries(prev =>
      prev.filter(entry => !localIds.includes(entry.id))
    );
  }, []);

  // The server sorts by content (category, issue, team, time), not by
  // creation time, so a fresh optimistic row only belongs on page 0.
  const displayedEntries =
    page === 0 ? [...optimisticEntries, ...entries] : entries;

  return (
    <Flex direction="column" gap={6} className={styles.coverageHoursTab}>
      <div className={styles.sectionIntro}>
        <H5 className={styles.sectionTitle}>
          {t('web.admin.serviceRequest.settings.tabs.coverageHours')}
        </H5>
        <M className={styles.sectionDescription}>
          {t('web.admin.serviceRequest.settings.coverageHours.description')}
        </M>
      </div>
      {canManage && (
        <div className={styles.toolbar}>
          <Button
            variant="primary"
            size="large"
            testId="coverage-hours-create"
            onClick={handleOpenPanel}
          >
            {t(
              'web.admin.serviceRequest.settings.coverageHours.create.button'
            )}
          </Button>
        </div>
      )}

      {channel?._id && (
        <CoverageHoursTable
          entries={displayedEntries}
          totalRows={totalRows}
          loading={loading}
          error={error}
          canManage={canManage}
          page={page}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={handlePageSizeChange}
        />
      )}

      {channel?._id && (
        <CreateCoverageHoursPanel
          isOpen={isPanelOpen}
          onClose={handleClosePanel}
          channelId={channel._id}
          channel={channel}
          categories={categories}
          onOptimisticAdd={handleOptimisticAdd}
          onCreateSuccess={handleCreateSuccess}
          onCreateError={handleCreateError}
        />
      )}
    </Flex>
  );
}
